import { useState } from "react";
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Badge,
  Select,
  Banner,
  TextContainer,
  Divider,
  InlineStack,
  BlockStack,
} from "@shopify/polaris";

interface Outcome {
  key: string;
  type: "message" | "discount_code" | "free_shipping" | "loyalty_points";
  weight: number;
  payload?: Record<string, any>;
}

interface RewardPolicyV1 {
  version: "1.0";
  outcomes: Outcome[];
  caps?: {
    perCustomerPerDay?: number;
    perDevicePerDay?: number;
  };
  budget?: {
    scope: "DAILY" | "WEEKLY" | "MONTHLY";
    maxGrants?: number;
  };
}

interface Props {
  initialPolicy?: RewardPolicyV1;
  onChange: (policy: RewardPolicyV1) => void;
}

const DEFAULT_POLICY: RewardPolicyV1 = {
  version: "1.0",
  outcomes: [
    {
      key: "try_again",
      type: "message",
      weight: 50,
      payload: { text: "Try again!" },
    },
  ],
  caps: {
    perCustomerPerDay: 3,
    perDevicePerDay: 5,
  },
  budget: {
    scope: "DAILY",
    maxGrants: 1000,
  },
};

export function RewardPolicyBuilder({ initialPolicy, onChange }: Props) {
  const [policy, setPolicy] = useState<RewardPolicyV1>(initialPolicy || DEFAULT_POLICY);

  const updatePolicy = (updates: Partial<RewardPolicyV1>) => {
    const updated = { ...policy, ...updates };
    setPolicy(updated);
    onChange(updated);
  };

  const addOutcome = () => {
    const newOutcome: Outcome = {
      key: `outcome_${policy.outcomes.length + 1}`,
      type: "message",
      weight: 10,
      payload: { text: "You won!" },
    };
    updatePolicy({ outcomes: [...policy.outcomes, newOutcome] });
  };

  const updateOutcome = (index: number, updates: Partial<Outcome>) => {
    const outcomes = [...policy.outcomes];
    outcomes[index] = { ...outcomes[index], ...updates };
    updatePolicy({ outcomes });
  };

  const removeOutcome = (index: number) => {
    const outcomes = policy.outcomes.filter((_, i) => i !== index);
    updatePolicy({ outcomes });
  };

  const totalWeight = policy.outcomes.reduce((sum, o) => sum + o.weight, 0);

  const getOutcomeProbability = (weight: number) => {
    if (totalWeight === 0) return "0%";
    return `${((weight / totalWeight) * 100).toFixed(1)}%`;
  };

  return (
    <Card>
      <div style={{ padding: "1rem" }}>
        <p style={{ fontWeight: 600, marginBottom: "1rem" }}>Reward Policy Configuration</p>
        <TextContainer>
          <p>
            Define what rewards customers can win and how likely they are to win each one.
          </p>
        </TextContainer>

        <Divider />

        <div style={{ marginTop: "1.5rem" }}>
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <h3 className="Polaris-Text--headingMd">
                Outcomes ({policy.outcomes.length})
              </h3>
              <Badge>{`Total weight: ${totalWeight}`}</Badge>
            </InlineStack>

            {policy.outcomes.map((outcome, index) => (
              <Card key={index}>
                <div style={{ padding: "1rem" }}>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Outcome Key"
                      value={outcome.key}
                      onChange={(value) => updateOutcome(index, { key: value })}
                      placeholder="try_again"
                      autoComplete="off"
                    />
                    <Select
                      label="Type"
                      value={outcome.type}
                      options={[
                        { label: "Message Only", value: "message" },
                        { label: "Discount Code", value: "discount_code" },
                        { label: "Free Shipping", value: "free_shipping" },
                        { label: "Loyalty Points", value: "loyalty_points" },
                      ]}
                      onChange={(value) =>
                        updateOutcome(index, { type: value as Outcome["type"] })
                      }
                    />
                  </FormLayout.Group>

                  <FormLayout.Group>
                    <TextField
                      label="Weight"
                      type="number"
                      value={String(outcome.weight)}
                      onChange={(value) =>
                        updateOutcome(index, { weight: parseInt(value) || 0 })
                      }
                      suffix={`Probability: ${getOutcomeProbability(outcome.weight)}`}
                      autoComplete="off"
                    />
                  </FormLayout.Group>

                  {outcome.type === "message" && (
                    <TextField
                      label="Message Text"
                      value={outcome.payload?.text || ""}
                      onChange={(value) =>
                        updateOutcome(index, {
                          payload: { ...outcome.payload, text: value },
                        })
                      }
                      placeholder="Try again!"
                      autoComplete="off"
                    />
                  )}

                  {outcome.type === "discount_code" && (
                    <FormLayout.Group>
                      <TextField
                        label="Discount Value"
                        type="number"
                        value={outcome.payload?.value?.toString() || "10"}
                        onChange={(value) =>
                          updateOutcome(index, {
                            payload: {
                              ...outcome.payload,
                              value: parseFloat(value) || 0,
                            },
                          })
                        }
                        autoComplete="off"
                      />
                      <Select
                        label="Value Type"
                        value={outcome.payload?.valueType || "percentage"}
                        options={[
                          { label: "Percentage", value: "percentage" },
                          { label: "Fixed Amount", value: "fixed_amount" },
                        ]}
                        onChange={(value) =>
                          updateOutcome(index, {
                            payload: { ...outcome.payload, valueType: value },
                          })
                        }
                      />
                    </FormLayout.Group>
                  )}

                  {outcome.type === "loyalty_points" && (
                    <TextField
                      label="Points Amount"
                      type="number"
                      value={outcome.payload?.points?.toString() || "100"}
                      onChange={(value) =>
                        updateOutcome(index, {
                          payload: {
                            ...outcome.payload,
                            points: parseInt(value) || 0,
                          },
                        })
                      }
                      autoComplete="off"
                    />
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button tone="critical" onClick={() => removeOutcome(index)}>
                      Remove Outcome
                    </Button>
                  </div>
                </FormLayout>
                </div>
              </Card>
            ))}

            <Button onClick={addOutcome}>Add Outcome</Button>
          </BlockStack>
        </div>

        <Divider />

        <div style={{ marginTop: "1.5rem" }}>
          <h3 className="Polaris-Text--headingMd" style={{ marginBottom: "1rem" }}>
            Caps & Limits
          </h3>

          <FormLayout>
            <FormLayout.Group>
              <TextField
                label="Per Customer Per Day"
                type="number"
                value={policy.caps?.perCustomerPerDay?.toString() || ""}
                onChange={(value) =>
                  updatePolicy({
                    caps: {
                      ...policy.caps,
                      perCustomerPerDay: value ? parseInt(value) : undefined,
                    },
                  })
                }
                placeholder="Unlimited"
                helpText="Max rewards a customer can win per day"
                autoComplete="off"
              />
              <TextField
                label="Per Device Per Day"
                type="number"
                value={policy.caps?.perDevicePerDay?.toString() || ""}
                onChange={(value) =>
                  updatePolicy({
                    caps: {
                      ...policy.caps,
                      perDevicePerDay: value ? parseInt(value) : undefined,
                    },
                  })
                }
                placeholder="Unlimited"
                helpText="Max rewards per device per day"
                autoComplete="off"
              />
            </FormLayout.Group>
          </FormLayout>
        </div>

        <Divider />

        <div style={{ marginTop: "1.5rem" }}>
          <h3 className="Polaris-Text--headingMd" style={{ marginBottom: "1rem" }}>
            Budget
          </h3>

          <FormLayout>
            <FormLayout.Group>
              <Select
                label="Budget Scope"
                value={policy.budget?.scope || "DAILY"}
                options={[
                  { label: "Daily", value: "DAILY" },
                  { label: "Weekly", value: "WEEKLY" },
                  { label: "Monthly", value: "MONTHLY" },
                ]}
                onChange={(value) =>
                  updatePolicy({
                    budget: {
                      scope: value as "DAILY" | "WEEKLY" | "MONTHLY",
                      maxGrants: policy.budget?.maxGrants,
                    },
                  })
                }
              />
              <TextField
                label="Max Grants"
                type="number"
                value={policy.budget?.maxGrants?.toString() || ""}
                onChange={(value) =>
                  updatePolicy({
                    budget: {
                      scope: policy.budget?.scope || "DAILY",
                      maxGrants: value ? parseInt(value) : undefined,
                    },
                  })
                }
                placeholder="Unlimited"
                helpText="Total rewards that can be granted"
                autoComplete="off"
              />
            </FormLayout.Group>
          </FormLayout>
        </div>

        {totalWeight === 0 && (
          <div style={{ marginTop: "1.5rem" }}>
            <Banner tone="warning">
              Total weight is 0. Add at least one outcome with a positive weight.
            </Banner>
          </div>
        )}
      </div>
    </Card>
  );
}
